import { useCallback, useEffect, useRef, useState } from "react";

import { MAPS_COUNTRY, MAP_ORIGIN, MARKET_BOUNDS } from "../config/market";
import { useGoogleMaps } from "../contexts/GoogleMapsContext";
import reportClientError from "../utils/clientTelemetry";

function textOf(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value.text === "string") return value.text;
  return typeof value.toString === "function" ? value.toString() : "";
}

function normalizeNewSuggestion(suggestion) {
  const prediction = suggestion?.placePrediction;
  if (!prediction) return null;
  const main = textOf(prediction.mainText);
  const secondary = textOf(prediction.secondaryText);
  const description = textOf(prediction.text) || [main, secondary].filter(Boolean).join(" ");
  return {
    place_id: prediction.placeId,
    description,
    structured_formatting: {
      main_text: main || description,
      secondary_text: secondary,
    },
    placePrediction: prediction,
  };
}

function legacyRequest(input) {
  return {
    input,
    componentRestrictions: { country: MAPS_COUNTRY },
    location: { lat: () => MAP_ORIGIN.lat, lng: () => MAP_ORIGIN.lng },
    radius: 200 * 1000,
    strictBounds: true,
  };
}

async function fetchLegacySuggestions(input) {
  const service = new window.google.maps.places.AutocompleteService();
  return new Promise((resolve, reject) => {
    service.getPlacePredictions(legacyRequest(input), (results, status) => {
      if (status === "OK") resolve(results || []);
      else if (status === "ZERO_RESULTS") resolve([]);
      else reject(new Error(`Address suggestions failed (${status || "UNKNOWN"})`));
    });
  });
}

async function fetchSuggestions(input, sessionToken) {
  const AutocompleteSuggestion = window.google?.maps?.places?.AutocompleteSuggestion;
  if (AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
    const request = {
      input,
      includedRegionCodes: [MAPS_COUNTRY],
      origin: MAP_ORIGIN,
      sessionToken,
      ...(MARKET_BOUNDS
        ? {
            locationRestriction: {
              west: MARKET_BOUNDS.minLng,
              east: MARKET_BOUNDS.maxLng,
              south: MARKET_BOUNDS.minLat,
              north: MARKET_BOUNDS.maxLat,
            },
          }
        : {}),
    };
    try {
      const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      return (response?.suggestions || []).map(normalizeNewSuggestion).filter(Boolean);
    } catch (error) {
      // Some existing Google Cloud projects have the legacy Places API enabled
      // but not Places API (New). Keep checkout usable while that external
      // migration is completed, and emit telemetry so the fallback is visible.
      reportClientError(error, { area: "google_places_new_api_fallback" });
      return fetchLegacySuggestions(input);
    }
  }

  reportClientError("New Places autocomplete is unavailable; using compatibility mode", {
    area: "google_places_legacy_fallback",
  });
  return fetchLegacySuggestions(input);
}

/**
 * Autocomplete adapter with the small interface the existing address fields
 * need. It prefers AutocompleteSuggestion (Places API New) and only uses the
 * legacy service as a compatibility fallback.
 */
export default function useMarketPlacesAutocomplete({
  debounce = 200,
  defaultValue = "",
} = {}) {
  const { isLoaded, error: mapsError } = useGoogleMaps();
  const [value, setInputValue] = useState(defaultValue);
  const [suggestions, setSuggestions] = useState({
    loading: false,
    status: "",
    data: [],
  });
  const [error, setError] = useState(null);
  const shouldFetchRef = useRef(false);
  const requestIdRef = useRef(0);
  const sessionTokenRef = useRef(null);

  const clearSuggestions = useCallback(() => {
    requestIdRef.current += 1;
    sessionTokenRef.current = null;
    setSuggestions({ loading: false, status: "", data: [] });
  }, []);

  const setValue = useCallback(
    (nextValue, shouldFetch = true) => {
      setInputValue(nextValue);
      requestIdRef.current += 1;
      shouldFetchRef.current = shouldFetch;
      setError(null);
      if (!shouldFetch || !String(nextValue || "").trim()) clearSuggestions();
      else setSuggestions({ loading: true, status: "", data: [] });
    },
    [clearSuggestions]
  );

  useEffect(() => {
    const query = String(value || "").trim();
    if (!isLoaded || !shouldFetchRef.current || !query) return undefined;

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(async () => {
      setSuggestions((current) => ({ ...current, loading: true }));
      try {
        if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
          sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
        const data = await fetchSuggestions(query, sessionTokenRef.current);
        if (requestId !== requestIdRef.current) return;
        setSuggestions({
          loading: false,
          status: data.length ? "OK" : "ZERO_RESULTS",
          data,
        });
      } catch (requestError) {
        if (requestId !== requestIdRef.current) return;
        reportClientError(requestError, { area: "google_places_autocomplete" });
        setError(requestError);
        setSuggestions({ loading: false, status: "ERROR", data: [] });
      }
    }, debounce);

    return () => {
      window.clearTimeout(timer);
      requestIdRef.current += 1;
    };
  }, [debounce, isLoaded, value]);

  return {
    ready: isLoaded,
    value,
    suggestions,
    setValue,
    clearSuggestions,
    error: error || mapsError,
  };
}
